import { LegalPage } from "./LegalPage";
import { useAppPreferences } from "../../lib/preferences";

export function PrivacyPage() {
  const { direction, resolvedLanguage, t } = useAppPreferences();
  return (
    <LegalPage
      eyebrow="Legal"
      lastUpdated="September 2026"
      title="PRIVACY POLICY"
    >
      <p>
        This Privacy Policy explains how Gaamed ("we", "us", "our") collects, uses, and shares
        information when you use the Gaamed platform (the "Service"). We are committed to
        protecting your personal data and processing it fairly and transparently.
      </p>

      <h2>1. Information We Collect</h2>
      <h3>Information you provide</h3>
      <ul>
        <li>
          <strong>Account information</strong> — name, email address, and password when you
          register.
        </li>
        <li>
          <strong>Venue information</strong> — venue name, address, description, unit types,
          operating hours and policy details provided by venue operators.
        </li>
        <li>
          <strong>Booking information</strong> — the venue, unit, date, time and duration of
          bookings you create.
        </li>
        <li>
          <strong>Communications</strong> — messages you send to us via email or support
          channels.
        </li>
      </ul>
      <h3>Information collected automatically</h3>
      <ul>
        <li>
          <strong>Usage data</strong> — pages visited, features used, timestamps and session
          durations within the platform.
        </li>
        <li>
          <strong>Device and browser data</strong> — browser type, operating system, IP
          address and language preferences.
        </li>
        <li>
          <strong>Cookies and session tokens</strong> — see the Cookies section below.
        </li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide, operate and maintain the Service.</li>
        <li>Process and manage bookings and gaming sessions.</li>
        <li>
          Send booking confirmations, QR check-in codes and notifications related to your
          account or bookings.
        </li>
        <li>Facilitate communication between players and venue operators as necessary for bookings.</li>
        <li>Detect and prevent fraud, abuse and security incidents.</li>
        <li>Comply with legal obligations.</li>
        <li>Improve the platform based on aggregated usage patterns.</li>
      </ul>
      <p>
        We do not sell your personal information to third parties. We do not use your data
        for behavioural advertising.
      </p>

      <h2>3. Sharing Your Information</h2>
      <p>We share your information only in the following circumstances:</p>
      <ul>
        <li>
          <strong>With venue operators</strong> — when you make a booking, the venue operator
          receives the details necessary to fulfil the booking (your name, booking time, unit
          and QR check-in code).
        </li>
        <li>
          <strong>With service providers</strong> — we may use third-party providers for
          hosting, email delivery and analytics. These providers are contractually bound to
          use your data only to provide services to us.
        </li>
        <li>
          <strong>Legal requirements</strong> — we may disclose your information if required
          by law, court order or government authority.
        </li>
        <li>
          <strong>Business transfers</strong> — in the event of a merger, acquisition or sale
          of assets, your information may be transferred as part of that transaction.
        </li>
      </ul>

      <h2>4. Data Retention</h2>
      <p>
        We retain your account information for as long as your account is active. Some records
        must be kept after account deletion for legal, tax, fraud-prevention, and
        dispute-resolution purposes, but they are anonymized so they no longer identify you. See
        the Account Deletion and Data Retention section below for details.
      </p>

      <section aria-labelledby="app-updates-contacts-title" dir={direction} id="app-updates-contacts" lang={resolvedLanguage}>
        <h3 id="app-updates-contacts-title">{t("onboarding.privacy.title")}</h3>
        <p>{t("onboarding.privacy.body")}</p>
        <p>{t("onboarding.form.dataPolicy")}</p>
        <p>{t("onboarding.privacy.separate")}</p>
        <p>
          {t("onboarding.form.deletion")}{" "}
          <a dir="ltr" href="mailto:support@gaamed.com">support@gaamed.com</a>.
        </p>
      </section>

      <h2>5. Account Deletion and Data Retention</h2>
      <p>
        You can request deletion of your Gaamed account and personal data at any time by visiting{" "}
        <a href="https://gaamed.com/legal/account-deletion">
          https://gaamed.com/legal/account-deletion
        </a>
        . You must be signed in with the customer account you want to delete. Deletion is not
        instant: we review each request to confirm there are no active bookings, sessions, or
        pending food orders, and we aim to complete the review within 30 days.
      </p>
      <p>When your deletion request is approved, we permanently delete:</p>
      <ul>
        <li>
          Your Gaamed account, profile, email address, password, and authentication tokens.
        </li>
        <li>Your saved venues and preferences.</li>
        <li>Password-reset tokens and email-verification codes.</li>
        <li>
          The comment text on any venue reviews you left. Star ratings are retained but are no
          longer linked to your identity.
        </li>
        <li>
          Your personal information in booking and session records, including your name, email,
          and phone number.
        </li>
      </ul>
      <p>
        The following data is anonymized and retained only for the limited purposes described
        below:
      </p>
      <ul>
        <li>
          <strong>Receipts and payment transactions</strong> — amounts, currency, payment
          method, timestamps, venue, and provider are kept for tax and legal compliance. In Egypt,
          financial records are typically retained for up to five years. Your name, email, and
          phone number are removed.
        </li>
        <li>
          <strong>Booking and session metadata</strong> — venue, unit, timing, and financial data
          are kept for dispute resolution and platform security. Your identity is removed and the
          records are no longer linked to your user account.
        </li>
        <li>
          <strong>Account-deletion audit log</strong> — a minimal record of the request, review
          decision, and completion timestamp is kept for security and legal accountability. This
          log contains no remaining personal information.
        </li>
      </ul>
      <p>
        If you have active bookings, an active gaming session, or pending food orders, you will
        need to cancel or complete them before we can delete your account. Provider, admin, and
        support accounts must use a separate offboarding process; the self-service page is for
        customer accounts only.
      </p>
      <p>
        For questions about deletion or data retention, contact us at{" "}
        <a href="mailto:privacy@gaamed.com">privacy@gaamed.com</a>.
      </p>

      <h2>6. Your Rights</h2>
      <p>Depending on your jurisdiction, you may have the right to:</p>
      <ul>
        <li>
          <strong>Access</strong> — request a copy of the personal data we hold about you.
        </li>
        <li>
          <strong>Rectification</strong> — correct inaccurate or incomplete data.
        </li>
        <li>
          <strong>Erasure</strong> — request deletion of your personal data, subject to legal
          retention obligations.
        </li>
        <li>
          <strong>Restriction</strong> — request that we limit the processing of your data in
          certain circumstances.
        </li>
        <li>
          <strong>Portability</strong> — receive your data in a structured, machine-readable
          format.
        </li>
        <li>
          <strong>Objection</strong> — object to processing based on legitimate interests.
        </li>
      </ul>
      <p>
        To exercise any of these rights, contact us at{" "}
        <a href="mailto:privacy@gaamed.com">privacy@gaamed.com</a>. We will respond within 30
        days.
      </p>

      <h2>7. Cookies and Session Tokens</h2>
      <p>
        We use server-issued session cookies to authenticate users. These cookies are
        HTTP-only and secure — they cannot be accessed by JavaScript and are transmitted only
        over HTTPS.
      </p>
      <p>
        We do not use tracking cookies, advertising cookies or third-party analytics cookies
        that identify individual users across other websites.
      </p>
      <p>
        You may disable cookies in your browser settings, but this will prevent you from
        signing in to the platform.
      </p>

      <h2>8. Security</h2>
      <p>
        We implement industry-standard technical and organisational measures to protect your
        personal data, including encrypted connections (HTTPS), secure session management and
        access controls that limit data access to authorised personnel.
      </p>
      <p>
        No method of transmission over the internet or method of electronic storage is 100%
        secure. We cannot guarantee absolute security, but we will notify you promptly in the
        event of a data breach affecting your personal data.
      </p>

      <h2>9. Children</h2>
      <p>
        The Service is not directed at children under the age of 13. We do not knowingly
        collect personal information from children. If you believe we have inadvertently
        collected information from a child, please contact us immediately and we will delete
        it.
      </p>

      <h2>10. International Transfers</h2>
      <p>
        Gaamed is operated primarily in Egypt. Your data may be processed by our service
        providers in other countries. Where we transfer personal data internationally, we take
        steps to ensure appropriate safeguards are in place in accordance with applicable data
        protection laws.
      </p>

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of material
        changes by posting a notice on the platform and, where appropriate, by email. The date
        at the top of this page indicates when the Policy was last revised.
      </p>

      <h2>12. Contact</h2>
      <p>
        For privacy-related questions, requests or complaints, contact our Data Protection
        contact at: <a href="mailto:privacy@gaamed.com">privacy@gaamed.com</a>
      </p>
      <p>
        Gaamed<br />
        Cairo, Egypt
      </p>
    </LegalPage>
  );
}
